import { useState } from 'react';
import ReactModal from 'react-modal';

import { cn } from 'lib/helpers/utility';

import Button from 'components/shared/Button/Button';
import Checkbox from 'components/shared/Checkbox/Checkbox';
import Icon from 'components/shared/Icon/Icon';

/**
 * @param {import('./lib/propTypes').ConfirmProps}
 */

const Confirm = ({
  icon,
  iconSize = 24,
  iconColor,
  variant = 'primary',
  title,
  message,
  children,
  primaryBtnTxt = 'Confirm',
  secondaryBtnTxt = 'Cancel',
  isOpen = false,
  handleOpen = false, // recommended to use isOpen instead of handleOpen
  handleContinue,
  handleClose,
  loading,
  customClassName,
  overlayClassName = null,
  childrenClassName,
  disabled = false,
  footer,
  isFooter = true,
  dontShowAgainCheck = false,
  continueEditing = false,
  ...rest
}) => {
  const [dontShowAgain, setDontShowAgain] = useState(false);

  return (
    <ReactModal
      isOpen={isOpen || handleOpen}
      onRequestClose={handleClose}
      contentLabel={title}
      ariaHideApp={false}
      className={cn(
        'relative z-[100] w-[456px] rounded-md border border-solid border-neutral-200 bg-white shadow-[0px_0px_16px_0px_rgba(55,65,69,0.08)] outline-none sm:!w-[90%] xs:!w-[calc(100%-32px)]',
        customClassName
      )}
      overlayClassName={cn('ModalOverlay', overlayClassName)}
      overlayRef={(node) => {
        node?.addEventListener('keypress', (event) => {
          if (event.key === 'Enter') {
            event.preventDefault();
            if (handleContinue) handleContinue();
          }
        });
      }}
      {...rest}>
      <div className="flex items-start justify-between gap-5 border-0 border-b border-solid border-neutral-200 px-5 pb-5 pt-6">
        {icon && (
          <div
            className={cn(
              'flex h-12 w-12 shrink-0 items-center justify-center rounded-full',
              `bg-${variant}-50`
            )}>
            <Icon icon={icon} size={iconSize} color={iconColor || variant} />
          </div>
        )}
        <div className="flex w-full justify-between gap-8">
          <div className="flex flex-col gap-0.5">
            <h3 className="text-base font-600 leading-6 text-neutral-800">{title}</h3>
            {message && (
              <p className="whitespace-pre-line text-sm leading-[22px] text-neutral-700">
                {message}
              </p>
            )}
          </div>
          <div
            data-qa="close-icon"
            className="flex h-7 w-7 shrink-0 cursor-pointer items-center justify-center hover:bg-neutral-50"
            onClick={handleClose}>
            <Icon icon="close-linear" size={20} />
          </div>
        </div>
      </div>
      {children && (
        <div
          className={cn(
            'border-0 border-b border-solid border-neutral-200 p-5',
            childrenClassName
          )}>
          {children}
        </div>
      )}
      {isFooter && typeof footer === 'object'
        ? footer
        : isFooter && (
            <div className="flex justify-end gap-x-3 px-4 py-3">
              {continueEditing && (
                <Button
                  size="small"
                  variant="link"
                  color="neutral"
                  icon="edit-2-mono"
                  iconColor="neutral"
                  text="Keep editing"
                  onClick={handleClose}
                />
              )}
              {dontShowAgainCheck && (
                <Checkbox
                  label="Don't show this message again"
                  innerLabelClassName="gap-[6px]"
                  labelTextColor="text-xs font-500 text-neutral-700"
                  size={16}
                  isChecked={dontShowAgain}
                  onChange={() => setDontShowAgain(!dontShowAgain)}
                />
              )}
              <Button
                data-qa="close-confirm-modal-btn"
                color="neutral"
                style="outlined"
                size="small"
                text={secondaryBtnTxt}
                onClick={handleClose}
              />
              <Button
                data-qa="proceed-confirm-modal-btn"
                size="small"
                color={variant}
                text={primaryBtnTxt}
                isLoading={loading}
                isDisabled={disabled}
                onClick={handleContinue}
              />
            </div>
          )}
    </ReactModal>
  );
};

export default Confirm;
